import FormErrors from "components/formParts/formErrors";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { isEmail } from "utils/validation";
import TextInput from "components/inputs/textInput";
import SelectInput from "components/inputs/selectInput";
import Button from "components/inputs/button";
import useLayout from "hooks/useLayout";
import Form from "components/forms/form"
import { ReactComponent as SendIcon } from "assets/icons/send.svg";
import ProcessingButton from "components/inputs/processingButton";
import Section from "components/layout/section";
import { AccessRole, InputMaxLength } from "types";
import Callout from "@/components/callout/callout";
import useAccess from "hooks/useAccess";
import { decodeFirebaseError } from "utils/firebaseHelpers";
import { logger } from "utils/secureLogger";
import useStorages from "hooks/useStorages";
import useStorageLimits from "hooks/useStorageLimits";

interface GrantAccessFormProps {
    storageId: string;
}

const GrantAccessForm: React.FC<GrantAccessFormProps> = ({ storageId }) => {

    const { t } = useTranslation()
    const { addToast, closeDialog } = useLayout()

    const [email, setEmail] = useState(``)
    const [role, setRole] = useState<AccessRole>(AccessRole.EDITOR)
    const [message, setMessage] = useState(``)
    const [errors, setErrors] = useState<string[]>([])

    const { createAccessInvitation, isCreating } = useAccess({ storageId })
    const { storage } = useStorages({ storageId })
    
    // Check if storage plan supports shared access
    const storagePlan = storage?.settings?.storagePlan
    const { canUseSharedAccess } = useStorageLimits({ storagePlan })

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setErrors([])
        if (!validate()) return
        
        try {
            await createAccessInvitation({ storageId, email, role, message })
            logger.info('Access invitation sent successfully', { storageId, email, role }, 'GrantAccess')
            addToast({ text: t('toastAccessInvitationSent'), colorScheme: 'green' })
            setEmail(``)
            setMessage(``)
            closeDialog()
        } catch (error: unknown) {
            const errorMessage = decodeFirebaseError(error instanceof Error ? error.message : String(error))
            setErrors([errorMessage])
            addToast({ text: t('toastAccessInvitationError'), colorScheme: 'red' })
            logger.error('Failed to send access invitation', error)
        }
    }

    const validate = () => {
        let newErrors = [] as string[]
        if (email === '') {
            newErrors = [...newErrors, t('emailIsRequired')]
        }
        if (!isEmail(email)) {
            newErrors = [...newErrors, t('emailIsInvalid')]
        }
        if (role === undefined) {
            newErrors = [...newErrors, t('roleIsRequired')]
        }
        setErrors(newErrors)
        return !(newErrors.length > 0)
    }

    // Handle email change.
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value)
    }

    // Handle message change.
    const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setMessage(e.target.value)
    }

    // Handle role change.
    const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setRole(e.target.value as AccessRole)
    }
    
    return (
        <Form
            onSubmit={handleSubmit}
            className={`group`}
            isLoading={isCreating}
        >
            <Section>
                {!canUseSharedAccess && (
                    <Callout type="warning">
                        {t('errorSharedAccessNotSupported')}
                    </Callout>
                )}
                <TextInput value={email} onChange={handleEmailChange} required label={t(`email`)} maxLength={InputMaxLength.medium} disabled={!canUseSharedAccess} />
                <SelectInput
                    label={t('labelRole')}
                    value={role}
                    options={[
                        { value: AccessRole.VIEWER, label: t('optionViewer') },
                        { value: AccessRole.EDITOR, label: t('optionEditor') },
                        { value: AccessRole.WORKER, label: t('optionWorker') }
                    ]}
                    onChange={handleRoleChange}
                    disabled={!canUseSharedAccess}
                    required />
                <Callout type="info">{t(`calloutAccessInvitaionMessageDescription`)}</Callout>
                <TextInput value={message} onChange={handleMessageChange} label={t(`labelMessage`)} maxLength={InputMaxLength.long} disabled={!canUseSharedAccess} />
                <div className={`input-holder`}>
                    {isCreating ? (
                        <ProcessingButton design="primary" />
                    ) : (
                        <Button wideOnMobile type="submit" design="primary" disabled={!canUseSharedAccess}><SendIcon /><span>{t('buttonSendInvitation')}</span></Button>
                    )}
                </div>
                {errors.length > 0 && <FormErrors errors={errors} />}
            </Section>
        </Form>
    )
}

export default GrantAccessForm



